import { BaseEffect, EasingFunction, Easing, CanvasDimensions, EffectResult } from './BaseEffect';
import { CropBox } from '../CropBox';

/**
 * ViewportEffect animates a crop/viewport rectangle over time.
 *
 * The `from` and `to` parameters are CropBox objects specifying the top-left corner (x, y) and size (width, height) of the visible area,
 * all as normalized values (0-1) relative to the source media's dimensions.
 *
 * This effect can be used to implement cropping, panning, zooming, or any combination thereof by animating the position and/or size of the viewport.
 *
 * For example:
 *   - To zoom in: animate width/height from 1 to a smaller value, keeping x/y centered.
 *   - To pan: animate x/y while keeping width/height constant.
 *   - To crop: set a static crop box.
 *
 * @param from - Starting CropBox (normalized: 0-1 for x, y, width, height)
 * @param to - Ending CropBox (normalized: 0-1 for x, y, width, height)
 * @param duration - Duration in seconds
 * @param at - Start time in seconds (optional)
 * @param easing - Easing function (optional)
 */
export class ViewportEffect extends BaseEffect {
    from: CropBox;
    to: CropBox;

    constructor(
        from: CropBox, 
        to: CropBox, 
        duration: number, 
        at?: number,
        easing: EasingFunction = Easing.spring
    ) {
        super('viewport', duration, at, easing);
        this.from = from;
        this.to = to;
    }

    getCurrentCrop(progress: number): CropBox {
        return {
            x: this.from.x + (this.to.x - this.from.x) * progress,
            y: this.from.y + (this.to.y - this.from.y) * progress,
            width: this.from.width + (this.to.width - this.from.width) * progress,
            height: this.from.height + (this.to.height - this.from.height) * progress
        };
    }

    apply(
        ctx: CanvasRenderingContext2D,
        dimensions: CanvasDimensions,
        progress: number,
        prev: EffectResult
    ): EffectResult {
        const currentCrop = this.getCurrentCrop(progress);
        
        // Calculate the crop rectangle in pixels using resolution dimensions
        const cropRect = {
            x: dimensions.resolutionWidth * currentCrop.x,
            y: dimensions.resolutionHeight * currentCrop.y,
            width: dimensions.resolutionWidth * currentCrop.width,
            height: dimensions.resolutionHeight * currentCrop.height
        };

        // Calculate scale and translation to fit the crop area
        const scaleX = dimensions.resolutionWidth / cropRect.width;
        const scaleY = dimensions.resolutionHeight / cropRect.height;
        const translateX = -cropRect.x * scaleX;
        const translateY = -cropRect.y * scaleY;

        // Apply the transformation
        ctx.translate(translateX, translateY);
        ctx.scale(scaleX, scaleY);

        return { shape: 'rect', rect: cropRect };
    }
} 